import React, { useState, useMemo } from "react";
import { Card, Typography, Row, Col, Input, Select, Button, Modal, Tag, Collapse, notification, Spin, Radio, Divider, Badge, Empty } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { deleteSummary, deleteAnswer, adoptSummary, adoptAnswer, unadoptSummary, unadoptAnswer, loadSummaries, loadAnswers } from "../store/app/app.slice";
import { FileTextOutlined, DeleteOutlined, EditOutlined, PlusOutlined, UserAddOutlined, UserDeleteOutlined, AppstoreOutlined, MessageOutlined, SearchOutlined } from "@ant-design/icons";
import CreateSummaryDialog from "../components/dialogs/CreateSummaryDialog";
import CreateAnswerDialog from "../components/dialogs/CreateAnswerDialog";
import { getUserProfile, getAuthenticatedUser } from "../utils/authHelper";
import { getTopicsLabel } from "../components/helper/labelHelper";


const { Title, Text } = Typography;
const { Panel } = Collapse;

export default function SummaryDashboard() {
  const dispatch = useDispatch();
  const { summaries, answers, isSummariesLoading, isAnswersLoading } = useSelector((state) => state.app);

  const [viewMode, setViewMode] = useState("summaries");
  const [searchTerm, setSearchTerm] = useState("");
  const [userFilter, setUserFilter] = useState([]);
  const [showCreateSummaryDialog, setShowCreateSummaryDialog] = useState(false);
  const [showCreateAnswerDialog, setShowCreateAnswerDialog] = useState(false);
  const [showEditSummaryDialog, setShowEditSummaryDialog] = useState(false);
  const [showEditAnswerDialog, setShowEditAnswerDialog] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerForSummary, setAnswerForSummary] = useState(null);
  const [defaultAnswerTitle, setDefaultAnswerTitle] = useState("");

  const userProfile = getUserProfile();
  const currentUserId = userProfile?.sub;
  const amjUser = getAuthenticatedUser();
  const isPortalManager = amjUser?.isPortalManager();

  const isMine = (item) =>
    item.createdBy === currentUserId ||
    (item.userIds && Array.from(item.userIds).includes(currentUserId));

  const matchesSearch = (item) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (item.titleUrdu && item.titleUrdu.toLowerCase().includes(s)) ||
      (item.titleEnglish && item.titleEnglish.toLowerCase().includes(s)) ||
      (item.answerEnglish && item.answerEnglish.toLowerCase().includes(s))
    );
  };

  const matchesUserFilter = (item) => {
    if (!userFilter || userFilter.length === 0) return true;
    return userFilter.some(userId =>
      item.createdBy === userId || (item.userIds && Array.from(item.userIds).includes(userId))
    );
  };

  const userOptions = useMemo(() => {
    const map = new Map();
    [...summaries, ...answers].forEach((item) => {
      if (item.createdBy && item.createdByName) {
        map.set(item.createdBy, item.createdByName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [summaries, answers]);

  const answersForSummary = (summaryId) =>
    answers.filter((a) => a.summaries && Array.from(a.summaries).some((s) => s.id === summaryId));

  const summariesForAnswer = (answer) =>
    answer.summaries ? Array.from(answer.summaries) : [];

  // Generate default title for answer based on summary
  const generateAnswerTitle = (summary) => {
    const summaryTitle = summary.titleEnglish || summary.titleUrdu || "Summary";
    const linkedAnswers = answersForSummary(summary.id);
    const counter = linkedAnswers.length + 1;
    return `${summaryTitle} - ${counter}`;
  };

  // Sort function: own creations first (by date desc), then adopted (by date desc)
  const sortItems = (items, currentUserId) => {
    return [...items].sort((a, b) => {
      const aIsCreator = a.createdBy === currentUserId;
      const bIsCreator = b.createdBy === currentUserId;

      // If one is creator and other is not, creator comes first
      if (aIsCreator && !bIsCreator) return -1;
      if (!aIsCreator && bIsCreator) return 1;

      // Both are same type (both creator or both adopted), sort by created date desc
      const aDate = a.created ? new Date(a.created).getTime() : 0;
      const bDate = b.created ? new Date(b.created).getTime() : 0;
      return bDate - aDate; // Most recent first
    });
  };

  // Filtered data
  const filteredSummaries = summaries.filter((s) => matchesSearch(s) && matchesUserFilter(s));
  const mySummaries = sortItems(filteredSummaries.filter(isMine), currentUserId);
  const otherSummaries = filteredSummaries.filter((s) => !isMine(s));

  const filteredAnswers = answers.filter((a) => matchesSearch(a) && matchesUserFilter(a));
  const myAnswers = sortItems(filteredAnswers.filter(isMine), currentUserId);
  const otherAnswers = filteredAnswers.filter((a) => !isMine(a));

  // Handlers
  const handleDeleteSummary = (summary) => {
    Modal.confirm({
      title: "Delete Summary",
      content: "Are you sure you want to delete this summary? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        dispatch(deleteSummary({
          finalData: { id: summary.id },
          callbackSuccess: () => notification.success({ message: "Summary deleted" }),
          callbackError: (err) => notification.error({ message: "Cannot delete summary", description: err }),
        }));
      },
    });
  };

  const handleDeleteAnswer = (answer) => {
    Modal.confirm({
      title: "Delete Answer",
      content: "Are you sure you want to delete this answer? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        dispatch(deleteAnswer({
          finalData: { id: answer.id },
          callbackSuccess: () => notification.success({ message: "Answer deleted" }),
          callbackError: (err) => notification.error({ message: "Cannot delete answer", description: err }),
        }));
      },
    });
  };

  const handleAdoptSummary = (summary) => {
    dispatch(adoptSummary({
      finalData: { id: summary.id },
      callbackSuccess: () => notification.success({ message: "Summary adopted" }),
      callbackError: (err) => notification.error({ message: "Failed to adopt summary", description: err }),
    }));
  };

  const handleAdoptAnswer = (answer) => {
    dispatch(adoptAnswer({
      finalData: { id: answer.id },
      callbackSuccess: () => notification.success({ message: "Answer adopted" }),
      callbackError: (err) => notification.error({ message: "Failed to adopt answer", description: err }),
    }));
  };

  const handleUnadoptSummary = (summary) => {
    dispatch(unadoptSummary({
      finalData: { id: summary.id },
      callbackSuccess: () => notification.success({ message: "Summary unadopted" }),
      callbackError: (err) => notification.error({ message: "Failed to unadopt summary", description: err }),
    }));
  };

  const handleUnadoptAnswer = (answer) => {
    dispatch(unadoptAnswer({
      finalData: { id: answer.id },
      callbackSuccess: () => notification.success({ message: "Answer unadopted" }),
      callbackError: (err) => notification.error({ message: "Failed to unadopt answer", description: err }),
    }));
  };

  // --- Render helpers ---

  const renderAnswerCard = (answer, mine) => {
    const isCreator = answer.createdBy === currentUserId;
    const isUrdu = answer.answerLanguages && Array.from(answer.answerLanguages).some((l) => l === "urdu" || l === "roman_urdu");

    return (
      <div
        key={answer.id}
        style={{
          padding: "10px 14px",
          margin: "6px 0",
          border: "1px solid #e8e8e8",
          borderLeft: mine ? "3px solid #755B48" : "3px solid #d9d9d9",
          borderRadius: 4,
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", gap: 4, marginRight: 8, flexShrink: 0 }}>
            {(mine && isCreator) || isPortalManager ? (
              <>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setSelectedAnswer(answer); setShowEditAnswerDialog(true); }}>Edit</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAnswer(answer)}>Delete</Button>
              </>
            ) : null}
            {mine && !isCreator && (
              <Button size="small" icon={<UserDeleteOutlined />} onClick={() => handleUnadoptAnswer(answer)}>Unadopt</Button>
            )}
            {!mine && (
              <Button size="small" icon={<UserAddOutlined />} onClick={() => handleAdoptAnswer(answer)}>Adopt</Button>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="arabic-font arabic-font-edit" style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{answer.titleEnglish || "Untitled Answer"}</div>
            <div
              className={isUrdu ? "arabic-font arabic-font-edit" : ""}
              style={{ color: "#555", fontSize: 13, marginTop: 4, lineHeight: 1.6, wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{ __html: answer.answerEnglish || "" }}
            />
            {answer.createdByName && (
              <Text type="secondary" style={{ fontSize: 11 }}>By: {answer.createdByName}</Text>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSummaryCard = (summary, mine) => {
    const isCreator = summary.createdBy === currentUserId;
    const linked = answersForSummary(summary.id);

    return (
      <Card
        key={summary.id}
        size="small"
        style={{ marginBottom: 12, borderRadius: 8, borderLeft: mine ? "3px solid #755B48" : "3px solid #d9d9d9" }}
        bodyStyle={{ padding: "12px 16px" }}
        hoverable
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 4, marginRight: 12, flexShrink: 0, marginTop: 2 }}>
            {(mine && isCreator) || isPortalManager ? (
              <>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setSelectedSummary(summary); setShowEditSummaryDialog(true); }}>Edit</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteSummary(summary)}>Delete</Button>
              </>
            ) : null}
            {mine && !isCreator && (
              <Button size="small" icon={<UserDeleteOutlined />} onClick={() => handleUnadoptSummary(summary)}>Unadopt</Button>
            )}
            {!mine && (
              <Button size="small" icon={<UserAddOutlined />} onClick={() => handleAdoptSummary(summary)}>Adopt</Button>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="arabic-font arabic-font-edit"
              style={{ fontSize: 16, lineHeight: 1.6, direction: "rtl", textAlign: "right", wordBreak: "break-word" }}
            >
              {summary.titleUrdu || summary.titleEnglish || "Untitled Summary"}
            </div>
            {summary.titleEnglish && summary.titleUrdu && (
              <Text type="secondary" style={{ fontSize: 12 }}>{summary.titleEnglish}</Text>
            )}
          </div>
        </div>

        {summary.topics && Array.from(summary.topics).length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Topics:</Text>
            {Array.from(summary.topics).map((t) => (
              <Tag key={t} color="processing" style={{ marginBottom: 2, fontSize: 11 }}>{getTopicsLabel(t)}</Tag>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
          {summary.createdByName ? (
            <Text type="secondary" style={{ fontSize: 11 }}>By: {summary.createdByName}</Text>
          ) : <span />}
          {mine && (
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setAnswerForSummary(summary.id);
                setDefaultAnswerTitle(generateAnswerTitle(summary));
                setShowCreateAnswerDialog(true);
              }}
            >
              Add Answer
            </Button>
          )}
        </div>

        {linked.length > 0 && (
          <Collapse size="small" ghost style={{ marginTop: 8 }}>
            <Panel
              header={<Text type="secondary" style={{ fontSize: 12 }}><MessageOutlined style={{ marginRight: 4 }} />Linked Answers ({linked.length})</Text>}
              key="answers"
            >
              {linked.map((a) => renderAnswerCard(a, mine))}
            </Panel>
          </Collapse>
        )}
      </Card>
    );
  };

  const renderAnswerStandaloneCard = (answer, mine) => {
    const isCreator = answer.createdBy === currentUserId;
    const isUrdu = answer.answerLanguages && Array.from(answer.answerLanguages).some((l) => l === "urdu" || l === "roman_urdu");
    const linkedSummaries = summariesForAnswer(answer);

    return (
      <Card
        key={answer.id}
        size="small"
        style={{ marginBottom: 12, borderRadius: 8, borderLeft: mine ? "3px solid #755B48" : "3px solid #d9d9d9" }}
        bodyStyle={{ padding: "12px 16px" }}
        hoverable
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div style={{ display: "flex", gap: 4, marginRight: 12, flexShrink: 0 }}>
            {(mine && isCreator) || isPortalManager ? (
              <>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setSelectedAnswer(answer); setShowEditAnswerDialog(true); }}>Edit</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAnswer(answer)}>Delete</Button>
              </>
            ) : null}
            {mine && !isCreator && (
              <Button size="small" icon={<UserDeleteOutlined />} onClick={() => handleUnadoptAnswer(answer)}>Unadopt</Button>
            )}
            {!mine && (
              <Button size="small" icon={<UserAddOutlined />} onClick={() => handleAdoptAnswer(answer)}>Adopt</Button>
            )}
          </div>
          <div className="arabic-font arabic-font-edit" style={{ fontSize: 15, fontWeight: 500, flex: 1, minWidth: 0 }}>{answer.titleEnglish || "Untitled Answer"}</div>
        </div>

        <div
          className={isUrdu ? "arabic-font arabic-font-edit" : ""}
          style={{ color: "#555", fontSize: 13, marginBottom: 8, lineHeight: 1.6, wordBreak: "break-word" }}
          dangerouslySetInnerHTML={{ __html: answer.answerEnglish || "" }}
        />

        {answer.topics && Array.from(answer.topics).length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Topics:</Text>
            {Array.from(answer.topics).map((t) => (
              <Tag key={t} color="processing" style={{ marginBottom: 2, fontSize: 11 }}>{getTopicsLabel(t)}</Tag>
            ))}
          </div>
        )}

        {linkedSummaries.length > 0 && (
          <Collapse size="small" ghost>
            <Panel
              header={<Text type="secondary" style={{ fontSize: 12 }}><FileTextOutlined style={{ marginRight: 4 }} />Linked Summaries ({linkedSummaries.length})</Text>}
              key="summaries"
            >
              {linkedSummaries.map((s) => (
                <div key={s.id} style={{ padding: "6px 10px", margin: "4px 0", background: "#fafafa", borderRadius: 4, border: "1px solid #f0f0f0" }}>
                  <div className="arabic-font arabic-font-extra-small" style={{ direction: "rtl", textAlign: "right" }}>
                    {s.titleUrdu || s.titleEnglish || "Untitled Summary"}
                  </div>
                </div>
              ))}
            </Panel>
          </Collapse>
        )}

        {answer.createdByName && (
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>By: {answer.createdByName}</Text>
          </div>
        )}
      </Card>
    );
  };

  const renderSummaryColumn = (items, mine, title, emptyText) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0, flex: 1 }}>{title}</Title>
        <Badge count={items.length} style={{ backgroundColor: mine ? '#755B48' : '#8c8c8c' }} />
      </div>
      {items.length > 0
        ? items.map((s) => renderSummaryCard(s, mine))
        : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
      }
    </div>
  );

  const renderAnswerColumn = (items, mine, title, emptyText) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0, flex: 1 }}>{title}</Title>
        <Badge count={items.length} style={{ backgroundColor: mine ? '#755B48' : '#8c8c8c' }} />
      </div>
      {items.length > 0
        ? items.map((a) => renderAnswerStandaloneCard(a, mine))
        : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
      }
    </div>
  );

  const loading = isSummariesLoading || isAnswersLoading;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Title level={2}>
            <FileTextOutlined style={{ marginRight: '8px' }} />
            Summary Dashboard
          </Title>
        </Col>
      </Row>

      {/* View toggle + filters */}
      <Card size="small" bodyStyle={{ padding: "12px 16px" }} style={{ marginBottom: '20px', borderRadius: 8 }}>
        <Row gutter={[16, 12]} align="middle">
          <Col>
            <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} buttonStyle="solid" size="middle">
              <Radio.Button value="summaries"><FileTextOutlined style={{ marginRight: 4 }} />Summaries</Radio.Button>
              <Radio.Button value="answers"><MessageOutlined style={{ marginRight: 4 }} />Answers</Radio.Button>
            </Radio.Group>
          </Col>
          <Col flex="auto">
            <Input
              placeholder="Search..."
              allowClear
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="arabic-font arabic-font-edit"
              style={{ width: '100%', borderRadius: '8px' }}
              prefix={<span style={{ color: '#8c8c8c' }}>🔍</span>}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              mode="multiple"
              placeholder="Filter by user"
              allowClear
              showSearch
              size="large"
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={userOptions}
              value={userFilter}
              onChange={setUserFilter}
            />
          </Col>
          <Col>
            {viewMode === "summaries" ? (
              <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setShowCreateSummaryDialog(true)}>
                New Summary
              </Button>
            ) : (
              <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setShowCreateAnswerDialog(true)}>
                New Answer
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Content */}
      <Spin spinning={loading}>
        {viewMode === "summaries" && (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              {renderSummaryColumn(mySummaries, true, "My Summaries", "No summaries yet")}
            </Col>
            <Col xs={24} lg={12}>
              {renderSummaryColumn(otherSummaries, false, "Other Users' Summaries", "No summaries from other users")}
            </Col>
          </Row>
        )}

        {viewMode === "answers" && (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              {renderAnswerColumn(myAnswers, true, "My Answers", "No answers yet")}
            </Col>
            <Col xs={24} lg={12}>
              {renderAnswerColumn(otherAnswers, false, "Other Users' Answers", "No answers from other users")}
            </Col>
          </Row>
        )}
      </Spin>

      {/* Dialogs */}
      <CreateSummaryDialog
        showModal={showCreateSummaryDialog}
        onHideModal={() => setShowCreateSummaryDialog(false)}
        onSummaryCreated={() => {
          setShowCreateSummaryDialog(false);
          dispatch(loadSummaries({}));
        }}
      />
      <CreateSummaryDialog
        showModal={showEditSummaryDialog}
        onHideModal={() => setShowEditSummaryDialog(false)}
        summary={selectedSummary}
        onSummaryUpdated={() => setShowEditSummaryDialog(false)}
      />
      <CreateAnswerDialog
        showModal={showCreateAnswerDialog}
        onHideModal={() => {
          setShowCreateAnswerDialog(false);
          setAnswerForSummary(null);
          setDefaultAnswerTitle("");
        }}
        summary={answerForSummary}
        defaultTitle={defaultAnswerTitle}
        onAnswerCreated={() => {
          setShowCreateAnswerDialog(false);
          setAnswerForSummary(null);
          setDefaultAnswerTitle("");
          dispatch(loadAnswers({}));
        }}
      />
      <CreateAnswerDialog
        key={selectedAnswer?.id}
        showModal={showEditAnswerDialog}
        onHideModal={() => setShowEditAnswerDialog(false)}
        answer={selectedAnswer}
        isUrduLanguage={selectedAnswer?.answerLanguages && Array.from(selectedAnswer.answerLanguages).some((l) => l === "urdu" || l === "roman_urdu")}
        onAnswerUpdated={() => setShowEditAnswerDialog(false)}
      />
    </div>
  );
}
